import { createAccessControl } from "better-auth/plugins/access";
import { defaultStatements } from "better-auth/plugins/organization/access";

/**
 * Platform permission statements (Agent OS plan D22, R7). One SoT for every
 * permission check: better-auth's organization endpoints, `authorize` in
 * `packages/api/modules/agent/capabilities`, and `useCan` on the web.
 *
 * `organization`, `member`, `invitation`, `team` and `ac` are better-auth's own
 * resources and gate its endpoints (e.g. `member.update` gates role changes).
 */
export const PLATFORM_STATEMENTS = {
	...defaultStatements,
	content: [
		"create",
		"read",
		"update",
		"delete",
		"publish",
		"publish_override_policy",
		"approve",
		"review_request",
		"review_comment",
		"preview_mint",
		"preview_revoke",
		"preview_use",
		"manage_collaborators",
	],
	learning: ["take", "review"],
	/**
	 * analyse-platform R43: read org analytics; record own interactions; create / edit / archive the
	 * org's dashboards and reports (P6b); export (P10).
	 */
	analytics: ["read", "record_own", "manage_org_dashboards", "export"],
	billing: ["read", "manage"],
	credits: ["spend", "approve"],
	agent: ["use", "act", "delegate", "schedule"],
	settings: ["read", "manage"],
	memory: ["read_org", "manage_org"],
	/** Own member profile fields (analyse-platform P4): member baseline, see `MEMBER_BASELINE_PERMISSIONS`. */
	profile: ["edit_own", "read_fields"],
	/** Templates of every kind (analyse-platform D31 / D36, R88): adopt / save your own; manage the org's. */
	templates: ["use", "manage"],
} as const;

export const ac = createAccessControl(PLATFORM_STATEMENTS);

export type PermissionResource = keyof typeof PLATFORM_STATEMENTS;

export type PermissionAction<
	R extends PermissionResource = PermissionResource,
> = (typeof PLATFORM_STATEMENTS)[R][number];

/** `"<resource>:<action>"`, e.g. `"content:publish"`. */
export type Permission = {
	[R in PermissionResource]: `${R}:${PermissionAction<R>}`;
}[PermissionResource];

export type PermissionSet = {
	[R in PermissionResource]?: readonly PermissionAction<R>[];
};

export const ALL_PERMISSIONS: readonly Permission[] = (
	Object.entries(PLATFORM_STATEMENTS) as [
		PermissionResource,
		readonly string[],
	][]
).flatMap(([resource, actions]) =>
	actions.map((action) => `${resource}:${action}` as Permission),
);

export function isPermission(value: string): value is Permission {
	return (ALL_PERMISSIONS as readonly string[]).includes(value);
}

export function flattenPermissionSet(set: PermissionSet): Permission[] {
	return (
		Object.entries(set) as [PermissionResource, readonly string[]][]
	).flatMap(([resource, actions]) =>
		actions.map((action) => `${resource}:${action}` as Permission),
	);
}
